<template>
  <div
    class="px-4 border border-border-primary rounded-sm text-headline text-left transition-colors flex justify-between items-center"
    :class="{
      'border-primary bg-primary/10': isSelected,
      'cursor-pointer': !disabled,
    }"
    :data-cy="`stip-${stipDefinition.id}`"
    @click="toggleStip"
  >
    <div class="flex flex-1 divide-primary/20 divide-x space-x-4">
      <div
        v-if="isSelected && !selectOnly"
        class="flex items-center space-x-2 py-3 pr-3"
      >
        <button
          v-if="!disabled"
          type="button"
          :disabled="stip && stip.count === 1"
          data-cy="decrease-count"
          @click.prevent.stop="updateStipCount('decrement')"
        >
          <icon-base
            :icon="IconSubtract"
            class="text-gray-500"
            view-box="0 -1 20 20"
          />
        </button>
        <div class="flex min-w-4 justify-center text-headline">
          {{ stip?.count }}
        </div>
        <button
          v-if="!disabled"
          type="button"
          :disabled="stip && stip.count >= MAX_REQUIRED_STIPS"
          class="relative top-0.5"
          data-cy="increase-count"
          @click.prevent.stop="updateStipCount('increment')"
        >
          <icon-base
            :icon="IconPlus"
            class="text-gray-500"
            view-box="20 30 40 20"
          />
        </button>
      </div>
      <div class="py-3 flex flex-1 justify-between items-center">
        <div class="flex items-center space-x-2">
          <div
            v-tooltip="shouldTruncateName ? stipDefinition.name : undefined"
            class="break-long-words"
          >
            {{ truncatedName }}
          </div>
          <div
            v-if="!disabled && !selectOnly && isSelected && stip"
            class="relative"
          >
            <button
              type="button"
              data-cy="edit-stip-description-button"
              @click.prevent.stop="
                showDescriptionEditor = !showDescriptionEditor
              "
            >
              <icon-base
                v-tooltip="stip?.description || $t('STIPS.MANAGE_DESCRIPTION')"
                :icon="stip?.description ? IconNoteAdded : IconAddNote"
                class="cursor-pointer relative top-[3px]"
              />
            </button>
            <div
              v-if="showDescriptionEditor"
              ref="popupRef"
              class="z-101"
              @click.stop
            >
              <stips-v2-description-editor
                :stip
                @description:saved="updateStipDescription($event)"
                @close="showDescriptionEditor = false"
              />
            </div>
          </div>
        </div>
        <icon-check-dashed
          v-if="!selectOnly && isSelected"
          v-tooltip="$t('DEALS.DOCUMENTS.IS_OPTIONAL')"
          class="size-[18px] transition-colors"
          :class="stip?.is_optional ? 'text-primary' : 'text-gray-500'"
          data-cy="toggle-stip-optional"
          @click.stop="toggleStipIsOptional"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef, watch } from "vue";

import { useTeleportV2 } from "@/hooks/elements";

import StipsV2DescriptionEditor from "./StipsV2DescriptionEditor.vue";
import IconAddNote from "@/components/icons/IconAddNote.vue";
import IconCheckDashed from "@/components/icons/IconCheckDashed.vue";
import IconNoteAdded from "@/components/icons/IconNoteAdded.vue";
import IconPlus from "@/components/icons/IconPlus.vue";
import IconSubtract from "@/components/icons/IconSubtract.vue";
import IconBase from "@/components/ui/IconBase.vue";

import type { FieldStip, StipDefinition } from "@/models/stips";

const props = defineProps<{
  stipDefinition: StipDefinition;
  selectOnly?: boolean;
  stip?: FieldStip;
  disabled?: boolean;
}>();

const emit = defineEmits<{
  toggle: [];
  update: [FieldStip];
}>();

const MAX_REQUIRED_STIPS = 255;
const LENGTH_BEFORE_NAME_TRUNCATE = 40;

const showDescriptionEditor = ref(false);

const popupRef = useTemplateRef<HTMLDivElement>("popupRef");

useTeleportV2({
  target: popupRef,
  to: "#menus",
  checkForOverflow: true,
  offset: { right: 0, top: 5 },
});

const isSelected = computed(() => Boolean(props.stip));

const shouldTruncateName = computed(() => {
  return props.stipDefinition.name.length > LENGTH_BEFORE_NAME_TRUNCATE;
});

const truncatedName = computed(() => {
  if (!shouldTruncateName.value) {
    return props.stipDefinition.name;
  }

  return `${props.stipDefinition.name.slice(0, LENGTH_BEFORE_NAME_TRUNCATE)}...`;
});

const updateStipCount = (direction: "increment" | "decrement") => {
  if (!props.stip) {
    return;
  }

  const currentCount = props.stip.count;
  const newCount =
    direction === "increment" ? currentCount + 1 : currentCount - 1;

  if (newCount < 1 || newCount > MAX_REQUIRED_STIPS) {
    return;
  }

  emit("update", {
    ...props.stip,
    count: newCount,
  });
};

const updateStipDescription = (newDescription: string) => {
  if (!props.stip) {
    return;
  }

  emit("update", {
    ...props.stip,
    description: newDescription,
  });
};

const toggleStipIsOptional = () => {
  if (!props.stip) {
    return;
  }

  emit("update", {
    ...props.stip,
    is_optional: !props.stip?.is_optional,
  });
};

const toggleStip = () => {
  if (props.disabled) {
    return;
  }

  emit("toggle");
};

watch(isSelected, (selected) => {
  if (selected) {
    return;
  }

  // useTeleportV2 only removes the teleported node on host unmount,
  // so an open popup gets orphaned in #menus when selection drops.
  popupRef.value?.remove();
  showDescriptionEditor.value = false;
});
</script>
